/**
 * SMS Queue Tab
 * Displays and manages queued SMS items
 */

import { useState, useMemo, useEffect } from 'react';
import {
  IconMessage,
  IconEye,
  IconEdit,
  IconTrash,
  IconRefresh,
  IconClock,
  IconPhone,
  IconCircleCheck,
  IconAlertTriangle,
  IconLoader,
} from '@tabler/icons-react';
import { Stack, Group, Text, Button, ActionIcon, Badge, SimpleGrid } from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import { useMemberLookup } from '../../_shared/useMemberLookup';
import { DataTable } from '../../../../../components/advanced/data';
import type { DataTableColumn } from '../../../../../components/advanced/data/DataTable';
import { EmptyState } from '../../../../../components/ui/data-display';
import { alert } from '../../../../../components/ui/providers/AlertProvider';
import type { SmsQueueTabProps, SmsQueueRow } from '../types';
import { mapSmsToRow, formatPhoneNumber, decodeSmsMessage } from '../types';
import { getStatusColor, formatDateTime } from '../../types';

type StatusKey = 'Pending' | 'Processing' | 'Sent' | 'Failed';

/** Collapse the various status strings into a stable bucket for the KPIs + filtering. */
function statusBucket(status?: string | null): StatusKey | 'Scheduled' | 'Other' {
  const s = (status || '').toLowerCase();
  if (s.includes('processing')) return 'Processing';
  if (s.includes('pending')) return 'Pending';
  if (s.includes('sent') || s.includes('delivered')) return 'Sent';
  if (s.includes('fail')) return 'Failed';
  if (s.includes('schedul')) return 'Scheduled';
  return 'Other';
}

const STATUS_KPIS: { key: StatusKey; label: string; color: string; icon: typeof IconClock }[] = [
  { key: 'Pending', label: 'Pending', color: 'yellow', icon: IconClock },
  { key: 'Processing', label: 'Processing', color: 'blue', icon: IconLoader },
  { key: 'Sent', label: 'Sent', color: 'teal', icon: IconCircleCheck },
  { key: 'Failed', label: 'Failed', color: 'red', icon: IconAlertTriangle },
];

export function SmsQueueTab({
  queueItems,
  loading,
  onRefresh,
  onView,
  onEdit,
  onDelete,
  onBulkDelete,
  onRetry,
}: SmsQueueTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusKey | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const { resolve, lookup } = useMemberLookup();

  const rows: SmsQueueRow[] = useMemo(() => {
    return queueItems.map(mapSmsToRow);
  }, [queueItems]);

  // Resolve member name/email for the visible recipient numbers (one batch call).
  useEffect(() => { resolve(rows.map((r) => r.cellNumber)); }, [rows, resolve]);

  // Reset to the first page whenever a filter changes so we never land on an empty page.
  useEffect(() => { setPage(1); }, [searchQuery, statusFilter]);

  // KPI counts per status bucket (drives the clickable cards).
  const counts = useMemo(() => {
    const c: Record<string, number> = { Pending: 0, Processing: 0, Sent: 0, Failed: 0, Scheduled: 0, Other: 0 };
    for (const r of rows) c[statusBucket(r.status)]++;
    return c;
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = statusFilter ? rows.filter((r) => statusBucket(r.status) === statusFilter) : rows;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((row) => {
        const decodedMessage = decodeSmsMessage(row.message);
        const c = lookup(row.cellNumber);
        return (
          row.cellNumber?.toLowerCase().includes(query) ||
          decodedMessage.toLowerCase().includes(query) ||
          row.status?.toLowerCase().includes(query) ||
          (c?.name || '').toLowerCase().includes(query) ||
          (c?.email || '').toLowerCase().includes(query)
        );
      });
    }
    return result;
  }, [rows, searchQuery, statusFilter, lookup]);

  const columns: DataTableColumn<SmsQueueRow>[] = useMemo(
    () => [
      {
        key: 'cellNumber',
        label: 'Recipient',
        sortable: true,
        width: 230,
        render: (value, row) => {
          const c = lookup(row.cellNumber);
          return (
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <IconPhone size={16} style={{ marginTop: 2, flexShrink: 0 }} />
              {c?.name ? (
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm" fw={500} truncate>{c.name}</Text>
                    {c.isMember && <Badge size="xs" variant="light" color="teal" style={{ flexShrink: 0 }}>Member</Badge>}
                  </Group>
                  <Text size="9px" c="dimmed" ff="monospace">{formatPhoneNumber(value) || 'N/A'}</Text>
                  {c.email && <Text size="9px" c="dimmed" truncate>{c.email}</Text>}
                </Stack>
              ) : (
                <Text size="sm" ff="monospace">{formatPhoneNumber(value) || 'N/A'}</Text>
              )}
            </Group>
          );
        },
      },
      {
        key: 'message',
        label: 'Message',
        sortable: true,
        width: 260,
        render: (value) => (
          <Text size="sm" lineClamp={2} style={{ maxWidth: 240 }}>
            {decodeSmsMessage(value) || '(No Message)'}
          </Text>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        filterable: true,
        width: 120,
        render: (value) => (
          <Badge
            variant="gradient"
            gradientVariant={getStatusColor(value as any)}
            pulse={value === 'Pending'}
          >
            {value}
          </Badge>
        ),
      },
      {
        key: 'priority',
        label: 'Priority',
        sortable: true,
        width: 90,
        render: (value) => (
          <Badge variant="outline" size="sm">
            {value ?? 5}
          </Badge>
        ),
      },
      {
        key: 'queuedDateTime',
        label: 'Queued',
        sortable: true,
        width: 160,
        render: (value) => (
          <Group gap="xs">
            <IconClock size={14} />
            <Text size="sm">{formatDateTime(value)}</Text>
          </Group>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 130,
        render: (_, row) => (
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                onView(row);
              }}
              title="View Details"
            >
              <IconEye size={16} />
            </ActionIcon>

            {row.status === 'Pending' && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row);
                }}
                title="Edit"
              >
                <IconEdit size={16} />
              </ActionIcon>
            )}

            {row.status === 'Failed' && (
              <ActionIcon
                variant="subtle"
                color="green"
                onClick={async (e) => {
                  e.stopPropagation();
                  const confirmed = await alert.confirm(
                    'Retry Failed SMS',
                    `Retry sending SMS to ${row.cellNumber}?`
                  );
                  if (confirmed) {
                    await onRetry(row.messageID);
                  }
                }}
                title="Retry"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            )}

            <ActionIcon
              variant="subtle"
              color="red"
              onClick={async (e) => {
                e.stopPropagation();
                const confirmed = await alert.confirm(
                  'Delete SMS',
                  `Delete SMS to ${row.cellNumber}?`,
                  {
                    confirmText: 'Delete',
                  }
                );
                if (confirmed) {
                  await onDelete(row.messageID);
                }
              }}
              title="Delete"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ),
      },
    ],
    [onView, onEdit, onDelete, onRetry]
  );

  const handleBulkDelete = async () => {
    const confirmed = await alert.confirm(
      'Delete Selected SMS',
      `Delete ${selectedRows.length} selected SMS message(s)?`,
      {
        confirmText: 'Delete All',
      }
    );

    if (confirmed) {
      // One request for the whole selection — not one call per row.
      await onBulkDelete(selectedRows.map(Number));
      setSelectedRows([]);
    }
  };

  if (!loading && queueItems.length === 0) {
    return (
      <EmptyState
        title="No Queued SMS"
        description="Your SMS queue is empty. Start by creating a test SMS in the Test tab."
        icon={<IconMessage size={48} />}
      />
    );
  }

  return (
    <Stack gap="md">
      {/* Clickable status KPIs — click to filter the table, click again (or Total) to clear. */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="sm">
        <Card
          withBorder
          padding="sm"
          onClick={() => setStatusFilter(null)}
          style={{
            cursor: 'pointer',
            borderColor: statusFilter === null ? 'var(--mantine-color-gray-5)' : undefined,
            borderWidth: statusFilter === null ? 2 : 1,
          }}
        >
          <Group gap="xs" wrap="nowrap">
            <IconMessage size={22} />
            <div>
              <Text size="lg" fw={700} lh={1.1}>{rows.length}</Text>
              <Text size="xs" c="dimmed">Total</Text>
            </div>
          </Group>
        </Card>
        {STATUS_KPIS.map((k) => {
          const Icon = k.icon;
          const active = statusFilter === k.key;
          return (
            <Card
              key={k.key}
              withBorder
              padding="sm"
              onClick={() => setStatusFilter(active ? null : k.key)}
              style={{
                cursor: 'pointer',
                borderColor: active ? `var(--mantine-color-${k.color}-6)` : undefined,
                borderWidth: active ? 2 : 1,
                backgroundColor: active ? `var(--mantine-color-${k.color}-light)` : undefined,
              }}
            >
              <Group gap="xs" wrap="nowrap">
                <Icon size={22} color={`var(--mantine-color-${k.color}-6)`} />
                <div>
                  <Text size="lg" fw={700} lh={1.1}>{counts[k.key]}</Text>
                  <Text size="xs" c="dimmed">{k.label}</Text>
                </div>
              </Group>
            </Card>
          );
        })}
      </SimpleGrid>

      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {filteredRows.length} {statusFilter ? `${statusFilter} ` : ''}message(s){statusFilter ? ` of ${rows.length}` : ' in queue'}
        </Text>
        <Group gap="xs">
          {selectedRows.length > 0 && (
            <Button color="red" variant="subtle" onClick={handleBulkDelete}>
              Delete Selected ({selectedRows.length})
            </Button>
          )}
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={onRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      <Card withBorder padding="md">
        <DataTable
          data={filteredRows}
          columns={columns}
          loading={loading}
          selectable
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search by phone number, message, or status..."
          searchValue={searchQuery}
          onSearch={setSearchQuery}
          onRowClick={(row) => onView(row)}
          pagination={{
            page,
            pageSize,
            total: filteredRows.length,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          gradientVariant="secondary"
          highlightOnHover
          striped
          withRowAnimations
          enableInternalSort
        />
      </Card>
    </Stack>
  );
}
